import { useCallback, useEffect, useMemo, useState } from 'react';

import { overtimeApprovalService } from '@/api/domains/overtime/overtimeApproval.service';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { useUI } from '@/hooks/ui/useUI';
import { useTranslation } from '@/i18n';
import type { OvertimeJustificationType } from '@/api/domains/overtime/overtimeApproval.types';


export interface UseOvertimeJustificationTypesResult {
  types: OvertimeJustificationType[];
  /** Opciones listas para el `Selector` (value = id como string). */
  options: { value: string; label: string }[];
  isLoading: boolean;
  /** Mensaje del backend cuando la carga falló, para dejar el selector en estado de error. */
  error: string | null;
  /** Catálogo cargado sin filas: el selector queda vacío y Justificar deshabilitada. */
  isEmpty: boolean;
  /** `true` si el tipo indicado exige comentario libre (código `OTRO`, resuelto por el backend). */
  requiresFreeComment: (justificationTypeId: number | null) => boolean;
}

/**
 * Catálogo de tipos de justificación del revisor (`caHHEEFlujoAprobacionComentario`, ASIS-5358).
 *
 * Una sola carga por montaje y solo cuando `enabled` es `true`: el endpoint está gateado por perfil
 * efectivo `REVISOR` y responde 403 a cualquier otro, así que pedirlo para un aprobador sería un
 * error garantizado. El backend devuelve **200 con lista vacía** cuando no hay tipos activos —el
 * escenario real hasta que se siembre el catálogo—, y ese caso NO es un error: la pantalla muestra
 * el selector vacío y deja Justificar deshabilitada, en vez de permitir enviar sin tipo.
 */
export function useOvertimeJustificationTypes(
  enabled: boolean,
): UseOvertimeJustificationTypesResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const { createSignal } = useAbortController();

  const [types, setTypes] = useState<OvertimeJustificationType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const signal = createSignal();
    setIsLoading(true);

    void (async () => {
      const response = await overtimeApprovalService.getJustificationTypes(signal);

      if (response.error?.code === 'ABORTED') return;
      setIsLoading(false);
      setHasLoaded(true);

      if (!response.success || !response.data) {
        setTypes([]);
        const message = showApiError(
          response,
          translate('attendance:overtimeApproval.detail.justify.typesLoadError'),
        );
        setError(message ?? translate('attendance:overtimeApproval.detail.justify.typesLoadError'));
        return;
      }

      setTypes(response.data);
      setError(null);
    })();
  }, [enabled, createSignal, showApiError, translate]);

  const options = useMemo(
    () => types.map((type) => ({ value: String(type.id), label: type.text })),
    [types],
  );

  const requiresFreeComment = useCallback(
    (justificationTypeId: number | null): boolean => (
      types.some((type) => type.id === justificationTypeId && type.requiresFreeComment)
    ),
    [types],
  );

  return {
    types,
    options,
    isLoading,
    error,
    isEmpty: hasLoaded && types.length === 0,
    requiresFreeComment,
  };
}

export default useOvertimeJustificationTypes;
